import { WorkerProfile } from "@/types/userTypes";

/** Provider auth-user id for send-request-quote `providerIds` (not the workerProfile _id). */
export function getProviderUserId(worker: any): string | null {
  if (!worker) return null;
  const raw = worker.user;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object") {
    const id = raw._id ?? raw.id;
    if (id != null) {
      const s = String(id);
      if (s && s !== "undefined" && s !== "[object Object]") return s;
    }
  }
  if (worker.userId != null) {
    const s = String(worker.userId);
    if (s && s !== "undefined") return s;
  }
  return null;
}

/** WorkerProfile document id (for profile APIs), not the auth user id. */
export function getWorkerProfileId(worker: any): string | null {
  if (!worker) return null;
  const id = worker._id ?? worker.id;
  if (id != null) {
    const s = String(id);
    if (s && s !== "undefined" && s !== "[object Object]") return s;
  }
  const wp = worker.user?.workerProfile;
  if (typeof wp === "string" && wp.trim()) return wp.trim();
  if (wp?._id != null) return String(wp._id);
  return null;
}

/**
 * Gets the list of missing required fields from a worker profile
 * Required fields: location, services, regions, description, payment
 * Payment: paymentType STRIPE or ATH; if ATH, athPhoneNumber is also required
 */
export const getMissingFields = (workerProfile: WorkerProfile | null | undefined): string[] => {
  const missingFields: string[] = [];
  
  if (!workerProfile) {
    return ['location', 'services', 'regions', 'description', 'payment'];
  }

  // 1. Check location (Google location or locationName)
  const hasLocation = (workerProfile.location && 
                      (typeof workerProfile.location === 'string' || 
                       (typeof workerProfile.location === 'object' && workerProfile.location !== null))) ||
                     (workerProfile.locationName && 
                      workerProfile.locationName.trim() !== '');
  if (!hasLocation) {
    missingFields.push('location');
  }

  // 2. Check services (servicesOffered array)
  const hasServices = workerProfile.servicesOffered && 
                      Array.isArray(workerProfile.servicesOffered) && 
                      workerProfile.servicesOffered.length > 0;
  if (!hasServices) {
    missingFields.push('services');
  }

  // 3. Check regions (Regions or RegionDetails)
  const hasRegions = (workerProfile.Regions && 
                     Array.isArray(workerProfile.Regions) && 
                     workerProfile.Regions.length > 0) ||
                    ((workerProfile as any).RegionDetails && 
                     Array.isArray((workerProfile as any).RegionDetails) && 
                     (workerProfile as any).RegionDetails.length > 0);
  if (!hasRegions) {
    missingFields.push('regions');
  }

  // 4. Check description
  const hasDescription = workerProfile.description && 
                        workerProfile.description.trim() !== '';
  if (!hasDescription) {
    missingFields.push('description');
  }

  // 5. Check payment method (STRIPE or ATH; ATH also needs phone)
  const paymentType = workerProfile.paymentType;
  const hasPaymentType = paymentType === 'STRIPE' || paymentType === 'ATH';
  if (!hasPaymentType) {
    missingFields.push('payment');
  } else if (paymentType === 'ATH') {
    const athDigits = String(workerProfile.athPhoneNumber || '').replace(/\D/g, '');
    if (!athDigits || athDigits.length < 10) {
      missingFields.push('payment');
    }
  }

  return missingFields;
};

/**
 * Checks if a worker profile has all required information completed
 * Local required fields: location, services, regions, description, payment
 * If backend sets isProfileCompleted === false, treat as incomplete even when local fields look filled
 */
export const isWorkerProfileComplete = (workerProfile: WorkerProfile | null | undefined): boolean => {
  if (!workerProfile) {
    return false;
  }

  if (getMissingFields(workerProfile).length > 0) {
    return false;
  }

  if (workerProfile.isProfileCompleted === false) {
    return false;
  }

  return true;
};
